import { computed, Injectable, signal } from '@angular/core';
import { objectToDictionary } from 'src/app/@shared/utils/utils';
import { TOOLS } from '../constants/tool.constants';
import { default_token_limits_agent, ITokenLimitsAgent, MESSAGE_TYPE, MessageItem, THREAD_METADATA_STATUS } from '../models/model';
import { IMessageFeedback } from '../models/types';
import { getToolName } from '../utils/utils';

export interface StreamingToolCall {
    id: string;
    name: string;
    args: string;
}

/**
 * Mirrors Python's ``PromptEnhancerInterruptKind`` and
 * ``PromptEnhancerInterruptSource`` enums.  Use these constants everywhere
 * instead of raw strings so a rename stays in one place.
 */
export const PROMPT_ENHANCEMENT_INTERRUPT = {
    /** Value of the ``kind`` field in every prompt-enhancement interrupt payload. */
    KIND: 'PROMPT_ENHANCEMENT' as const,
    /** Value of the ``source`` field set by ``PromptEnhancerMiddleware``. */
    SOURCE: 'prompt_enhancer_middleware' as const,
} as const;

/**
 * Mirrors Python's ``PromptEnhancerInterruptKind.PROMPT_REUSE_SUGGESTION``.
 * Fired when Milvus finds ≥1 saved prompt with cosine similarity ≥ 0.72.
 */
export const PROMPT_REUSE_INTERRUPT = {
    /** Value of the ``kind`` field in the reuse-suggestion interrupt payload. */
    KIND: 'PROMPT_REUSE_SUGGESTION' as const,
    /** Source string — same middleware that fires PROMPT_ENHANCEMENT. */
    SOURCE: 'prompt_enhancer_middleware' as const,
} as const;

/**
 * Single candidate returned by the backend's Milvus similarity search.
 * Each item carries enough info for the UI to let the user preview and select.
 */
export interface PromptReuseCandidateCreator {
    fullName?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
}

export interface PromptReuseCandidate {
    promptId: string;
    title: string;
    content: string;
    score: number;
    /** 'PERSONAL' | 'WORKSPACE' — visibility level of the prompt. */
    accessLevel?: string;
    /** Number of times this prompt has been used from the library. */
    timesUsed?: number;
    /** ISO 8601 string of the last time this prompt was updated. */
    updatedAt?: string | null;
    /** Creator user details. */
    creator?: PromptReuseCandidateCreator | null;
    /** Resolved group names this prompt belongs to (for the folder badge). */
    groups?: string[] | null;
}

/**
 * Payload delivered when the agent pauses to let the user review a
 * Markdown-formatted enhanced version of their request.  Emitted by the
 * ``enhance_prompt`` tool inside :class:`PromptEnhancerMiddleware`.
 */
export interface PromptEnhancementData {
    /** Source string of the interrupt — currently only ``'prompt_enhancer_middleware'``. */
    source?: string;
    /** Full Markdown brief the user needs to approve before execution begins. */
    markdown?: string;
}

export interface ToolGroupItem {
    isToolGroup: true;
    id: string;
    messages: MessageItem[];
    // For task groups: recursively grouped subagent messages visible while streaming
    nestedGroupedMessages?: (MessageItem | ToolGroupItem)[];
    isTask?: boolean;
    // The final tool result message for the task (present when the task has completed)
    taskResult?: MessageItem;
    // Pre-computed image URLs from tool messages (avoids repeated lookups in template)
    imageUrls: string[];
}

function collectImageUrls(messages: MessageItem[]): string[] {
    const urls: string[] = [];
    for (const msg of messages) {
        if (msg.type !== 'tool') continue;
        const url = msg['response_metadata']?.imageUrl || msg.additional_kwargs?.imageUrl;
        if (url) urls.push(url);
    }
    return urls;
}

const EXCLUDED_TOOLS = [TOOLS.WRITE_TODOS, TOOLS.PRESENT_FILES];

export function isSubagentNamespace(namespace: string[] | null | undefined): boolean {
    return Array.isArray(namespace) && namespace.length > 0 && namespace.some((s: string) => s.startsWith('tools:'));
}

export function getSubagentTaskId(namespace: string[] | null | undefined): string | null {
    if (!Array.isArray(namespace)) return null;
    const toolSegment = namespace.find((s: string) => s.startsWith('tools:'));
    return toolSegment ? toolSegment.split(':')[1] : null;
}

function isSubagentMessage(msg: MessageItem): boolean {
    const namespace = (msg as any).namespace ?? msg.additional_kwargs?.['namespace'];
    return isSubagentNamespace(namespace);
}

function isCanvasFileTool(msg: MessageItem, toolName: string): boolean {
    return [TOOLS.WRITE_FILE, TOOLS.EDIT_FILE].includes(toolName as TOOLS) &&
        ((msg.type === 'tool' && (msg.additional_kwargs?.['canvasContent'] || msg.additional_kwargs?.['args']?.['file_group'] === 'CANVAS')) ||
            (msg.type === 'ai' && (msg.tool_calls?.length ?? 0) > 0 && msg.tool_calls?.[0]?.args?.['file_group'] === 'CANVAS'));
}

/**
 * Groups a flat list of messages into a display-ready list of MessageItem | ToolGroupItem.
 *
 * - Regular tool calls and empty AI tool-call messages are merged into ToolGroupItems (accordions).
 * - Task tool calls trigger a nested ToolGroupItem: subagent messages between the task AI call and
 *   the task result are recursively grouped into `nestedGroupedMessages`.
 * - When the task result exists with content, `taskResult` is set and intermediate subagent messages
 *   are not rendered (they have already been pruned from the main messages array on completion).
 *
 * @param messages   The slice of messages to group (all messages for context lookups).
 * @param allMessages The full messages array used for tool-name lookups via tool_call_id.
 */
export function groupMessageList(
    messages: MessageItem[],
    allMessages: MessageItem[]
): (MessageItem | ToolGroupItem)[] {
    const grouped: (MessageItem | ToolGroupItem)[] = [];
    let i = 0;

    while (i < messages.length) {
        const msg = messages[i];
        const toolName = getToolName(msg, allMessages);
        const hasContent = !!(msg.content && msg.content.trim().length > 0);
        const isCanvasFile = isCanvasFileTool(msg, toolName);

        // Detect a task AI call: AI message with a `task` tool call and no visible content
        const isTaskAICall = msg.type === 'ai' &&
            (msg.tool_calls ?? []).some((tc: any) => tc.name === TOOLS.TASK) &&
            !hasContent;

        if (isTaskAICall) {
            // Collect all task tool call IDs from this AI message
            const taskToolCallIds = (msg.tool_calls ?? [])
                .filter((tc: any) => tc.name === TOOLS.TASK)
                .map((tc: any) => tc.id as string);

            // Scan forward: collect subagent messages and the task result(s)
            const subagentMessages: MessageItem[] = [];
            const taskResults: MessageItem[] = [];
            let j = i + 1;

            // Track tool call IDs from subagent AI messages so that their tool results
            // (placeholders and actual results) are captured even if they lack isSubagent flag.
            const subagentToolCallIds = new Set<string>();

            while (j < messages.length) {
                const next = messages[j];
                const nextToolName = getToolName(next, allMessages);

                // Task result: tool message whose tool_call_id matches one of the task call IDs.
                // Skip loading placeholders — they share the same tool_call_id but are not real results
                // and must not short-circuit the collection of subagent messages that follow.
                const isLoadingPlaceholder = next.status === 'loading' || !!(next.id?.endsWith('-loading'));
                if (next.type === 'tool' && next.tool_call_id && taskToolCallIds.includes(next.tool_call_id) && !isLoadingPlaceholder) {
                    taskResults.push(next);
                    j++;
                    if (taskResults.length >= taskToolCallIds.length) break;
                    continue;
                }

                // Stop collecting once any task results have started arriving
                if (taskResults.length > 0) break;

                const isSubagent = isSubagentMessage(next);

                // A tool result whose tool_call_id matches a previously seen subagent AI tool call
                // is owned by the task group even if the isSubagent flag is absent (e.g. placeholders
                // created by the data.messages handler before the isSubagent fix).
                const isSubagentToolResult =
                    next.type === 'tool' &&
                    next.tool_call_id != null &&
                    subagentToolCallIds.has(next.tool_call_id);

                if (isSubagent || isSubagentToolResult) {
                    // Register tool call IDs from subagent AI messages so their tool results
                    // are also captured even when the isSubagent flag is absent.
                    if (next.type === 'ai' && next.tool_calls) {
                        for (const tc of (next.tool_calls as any[])) {
                            if (tc.id) subagentToolCallIds.add(tc.id);
                        }
                    }
                    subagentMessages.push(next);
                    j++;
                } else {
                    // Non-subagent, non-task-result message terminates the task scope
                    break;
                }
            }

            // After the task results are collected, absorb any trailing subagent messages
            // that arrived after the task result (e.g. a subagent final-thought message).
            while (j < messages.length) {
                const trailing = messages[j];
                const isTrailingSubagent = isSubagentMessage(trailing);
                const isTrailingToolResult =
                    trailing.type === 'tool' &&
                    trailing.tool_call_id != null &&
                    subagentToolCallIds.has(trailing.tool_call_id);
                if (!isTrailingSubagent && !isTrailingToolResult) break;
                if (trailing.type === 'ai' && trailing.tool_calls) {
                    for (const tc of (trailing.tool_calls as any[])) {
                        if (tc.id) subagentToolCallIds.add(tc.id);
                    }
                }
                subagentMessages.push(trailing);
                j++;
            }

            // Build the task ToolGroupItem
            const allTaskMessages: MessageItem[] = [msg, ...subagentMessages, ...taskResults];
            const taskResult = taskResults.length > 0 && taskResults[0].content?.trim() ? taskResults[0] : undefined;
            // Recursively group the subagent messages for nested rendering
            const nestedGroupedMessages = groupMessageList(subagentMessages, allMessages);

            const taskGroup: ToolGroupItem = {
                isToolGroup: true,
                id: `group-${msg.id}`,
                messages: allTaskMessages,
                isTask: true,
                nestedGroupedMessages,
                taskResult,
                imageUrls: collectImageUrls(allTaskMessages)
            };
            grouped.push(taskGroup);

            i = j;
            continue;
        }

        // Regular (non-task) grouping logic
        //
        // isExcludedTool: AI message whose every tool call is in EXCLUDED_TOOLS — skipped entirely
        //   (not grouped, not rendered standalone).
        // isAbsorbedExcludedResult: tool result for an excluded tool (e.g. write_todos) that appears
        //   alongside non-excluded calls in the same AI turn.  It must be silently absorbed into the
        //   existing group to avoid breaking the chain for subsequent real tool results.
        const isExcludedTool = msg.type === MESSAGE_TYPE.AI && (msg.tool_calls?.length ?? 0) > 0 &&
            (msg.tool_calls ?? []).every((tc: any) => EXCLUDED_TOOLS.includes(tc.name as TOOLS));

        // Skip AI messages that only call excluded tools (e.g. write_todos-only turns) — they
        // carry no visible content and should not appear in the message list at all.
        if (isExcludedTool) {
            i++;
            continue;
        }

        const isArtifactTool = msg.type === MESSAGE_TYPE.TOOL && !!toolName &&
            !EXCLUDED_TOOLS.includes(toolName as TOOLS) && !isCanvasFile;
        // A tool result whose name is excluded (e.g. write_todos) but needs to be absorbed
        // into an existing group so it doesn't interrupt the chain of real tool results.
        const isAbsorbedExcludedResult = msg.type === MESSAGE_TYPE.TOOL && !!toolName &&
            EXCLUDED_TOOLS.includes(toolName as TOOLS) && !isCanvasFile;
        const isSubagent = isSubagentMessage(msg) &&
            msg.type !== MESSAGE_TYPE.HUMAN &&
            !EXCLUDED_TOOLS.includes(toolName as TOOLS) && !isCanvasFile;
        const isAIToolCallWithoutContent = msg.type === MESSAGE_TYPE.AI &&
            (msg.tool_calls?.length ?? 0) > 0 && !hasContent &&
            !isCanvasFile;

        if (isArtifactTool || isAIToolCallWithoutContent || isSubagent) {
            const last = grouped.length > 0 ? grouped[grouped.length - 1] : null;
            const lastIsRegularGroup = last && (last as ToolGroupItem).isToolGroup && !(last as ToolGroupItem).isTask;

            if (lastIsRegularGroup) {
                const group = last as ToolGroupItem;
                group.messages.push(msg);
                const url = msg.type === 'tool' ? (msg['response_metadata']?.imageUrl || msg.additional_kwargs?.imageUrl) : null;
                if (url) group.imageUrls.push(url);
            } else {
                const newGroup: ToolGroupItem = {
                    isToolGroup: true,
                    id: `group-${msg.id}`,
                    messages: [msg],
                    imageUrls: collectImageUrls([msg])
                };
                grouped.push(newGroup);
            }
        } else if (isAbsorbedExcludedResult) {
            // Skip excluded tool results (e.g. write_todos) entirely — do not absorb into the
            // group and do not render standalone.  Subsequent real tool results (e.g. ls) will
            // still find the group created by the preceding AI message because we only skip,
            // not push, so the group remains the last item in the array.
            i++;
            continue;
        } else {
            grouped.push(msg);
        }

        i++;
    }

    return grouped;
}

@Injectable({
    providedIn: 'root'
})
export class ThreadMessagesService {
    //#region Signals
    //NOTE: Clear these signals in clearMessages()
    private _messages = signal<MessageItem[]>([]);
    private _inputTokens = signal<number | null>(null);
    private _tokenLimitsAgent = signal<ITokenLimitsAgent>(default_token_limits_agent);
    private _messageFeedbacksByMessageId = signal<Record<string, IMessageFeedback>>({});
    private _threadMetadataStatus = signal<string | undefined>(undefined);
    private _threadPauseReason = signal<string | null>(null);
    private _streamingToolCalls = signal<Record<string, StreamingToolCall>>({});
    private _activeThreadId = signal<string>('');
    private _errorMessage = signal<string | undefined>(undefined);
    /** Non-null when the current INTERRUPTED status carries a PROMPT_ENHANCEMENT payload. */
    private _promptEnhancement = signal<PromptEnhancementData | null>(null);
    /** Non-null when the current INTERRUPTED status carries a PROMPT_REUSE_SUGGESTION payload. */
    private _promptReuseSuggestion = signal<PromptReuseCandidate[] | null>(null);
    //#endregion

    //#region Public Getters
    messages = this._messages.asReadonly();
    inputTokens = this._inputTokens.asReadonly();
    tokenLimitsAgent = this._tokenLimitsAgent.asReadonly();
    messageFeedbacksByMessageId = this._messageFeedbacksByMessageId.asReadonly();
    threadMetadataStatus = this._threadMetadataStatus.asReadonly();
    threadPauseReason = this._threadPauseReason.asReadonly();
    streamingToolCalls = this._streamingToolCalls.asReadonly();
    activeThreadId = this._activeThreadId.asReadonly();
    errorMessage = this._errorMessage.asReadonly();
    promptEnhancement = this._promptEnhancement.asReadonly();
    /** Readonly signal: non-null while waiting for user to act on a prompt-reuse card. */
    promptReuseSuggestion = this._promptReuseSuggestion.asReadonly();

    groupedMessages = computed(() => {
        const messages = this._messages();
        return groupMessageList(messages, messages);
    });
    //#endregion

    setMessages(messages: MessageItem[]): void {
        const filteredMessages = messages.filter(
            msg => msg?.additional_kwargs?.lc_source !== 'exlcude_from_ui'
        );
        this._messages.set(filteredMessages);
    }

    addMessages(messages: MessageItem[]): void {
        const filteredMessages = messages.filter(
            msg => msg?.additional_kwargs?.lc_source !== 'exlcude_from_ui'
        );
        this._messages.update(current => [...current, ...filteredMessages]);
    }

    updateMessage(messageId: string, updates: Partial<MessageItem>): void {
        this._messages.update(current =>
            current.map(msg =>
                msg.id === messageId ? { ...msg, ...updates } : msg
            )
        );
    }

    setMessageFeedbacksByMessageId(messageFeedbacks: IMessageFeedback[]): void {
        this._messageFeedbacksByMessageId.set(objectToDictionary(messageFeedbacks, 'messageId'));
    }

    addMessageFeedback(messageFeedback: IMessageFeedback): void {
        this._messageFeedbacksByMessageId.update(current => ({ ...current, [messageFeedback.messageId]: messageFeedback }));
    }

    setActiveThreadId(threadId: string): void {
        this._activeThreadId.set(threadId);
    }

    setPromptEnhancement(data: PromptEnhancementData | null): void {
        this._promptEnhancement.set(data);
    }

    /** Set (or clear) the prompt-reuse suggestion candidates for the current interrupt. */
    setPromptReuseSuggestion(candidates: PromptReuseCandidate[] | null): void {
        this._promptReuseSuggestion.set(candidates);
    }

    clearMessages(): void {
        this._messages.set([]);
        this._inputTokens.set(null);
        this._tokenLimitsAgent.set(default_token_limits_agent);
        this._messageFeedbacksByMessageId.set({});
        this._threadMetadataStatus.set(undefined);
        this._threadPauseReason.set(null);
        this._streamingToolCalls.set({});
        this._promptEnhancement.set(null);
        this._promptReuseSuggestion.set(null);
        this._activeThreadId.set('');
        this._errorMessage.set(undefined);
    }

    getMessageById(messageId: string): MessageItem | undefined {
        return this._messages().find(msg => msg.id === messageId);
    }

    findToolResultByToolCallId(toolCallId: string): MessageItem | undefined {
        return this._messages().find(
            msg => msg.type === 'tool' && msg.tool_call_id === toolCallId
        );
    }

    setTokenLimitsAgent(tokenLimitsAgent: ITokenLimitsAgent): void {
        this._tokenLimitsAgent.set(tokenLimitsAgent ?? default_token_limits_agent);
    }

    setInputTokens(inputTokens: number | null): void {
        this._inputTokens.set(inputTokens);
    }

    setThreadMetadataStatus(status: string | undefined): void {
        this._threadMetadataStatus.set(status);
        if (status !== THREAD_METADATA_STATUS.PAUSED) {
            this._threadPauseReason.set(null);
        }
        // Clear prompt-enhancement and reuse-suggestion data whenever the interrupt is resolved.
        if (status !== THREAD_METADATA_STATUS.INTERRUPTED) {
            this._promptEnhancement.set(null);
            this._promptReuseSuggestion.set(null);
        }
    }

    setThreadPauseReason(reason: string | null): void {
        this._threadPauseReason.set(reason);
    }

    updateStreamingToolCalls(toolCalls: StreamingToolCall[]): void {
        const dict: Record<string, StreamingToolCall> = {};
        for (const tc of toolCalls) {
            if (tc.id) {
                dict[tc.id] = tc;
            }
        }
        this._streamingToolCalls.set(dict);
    }

    clearStreamingToolCalls(): void {
        this._streamingToolCalls.set({});
    }

    setErrorMessage(message: string | undefined): void {
        this._errorMessage.set(message);
    }
}
